import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { IOverlayLoaderStore } from '../../../../../../modules/common/OverlayLoader.store';
import { Loader } from '../../../../../components/Loader';
import { AllStatusChart } from '../components/AllStatusChart';
import { AnnotationStatusChart } from '../components/AnnotationStatusChart';
import { AnswersChart } from '../components/AnswersChart';
import { EmptyStatistics } from '../components/EmptyStatistics';
import { OutOfDateMessage } from '../components/OutOfDateMessage';
import { IProjectDetailsStatisticsBl } from '../projectDetailsStatistics.bl';
import { IAnswerStatisticDto, ToolStatisticsViewMode } from '../projectDetailsStatistics.model';

export const ToolsStatisticsContentContainer = observer((): ReactElement => {
  const projectDetailsStatisticsBl = useInjection(IProjectDetailsStatisticsBl.$);
  const overlayLoader = useInjection(IOverlayLoaderStore.$);

  const store = projectDetailsStatisticsBl.store;

  if (store.toolStatisticsViewMode === ToolStatisticsViewMode.NONE) {
    return <EmptyStatistics />;
  }

  const toolStatistics = store.toolStatistics;
  const isUpToDate = toolStatistics.headers.isUpToDate;

  const toolStatisticsViewMode = store.toolStatisticsViewMode;
  const overlayIsLoading = overlayLoader.isSpinnerVisible('tool-statistics-loading') || false;
  const selectedToolStatistics = store.selectedToolStatistics;

  return (
    <div className='statistics'>
      <Loader isLoading={overlayIsLoading} />
      <OutOfDateMessage isUpToDate={isUpToDate} />
      <div className='scrollable-container'>
        {toolStatisticsViewMode === ToolStatisticsViewMode.All_TOOLS && (
          <AllStatusChart
            annotationStatuses={toolStatistics.allAnnotationTypeStatistics}
            title={'all_tools'}
          />
        )}
        {toolStatisticsViewMode === ToolStatisticsViewMode.ALL_QUESTIONS && (
          <AllStatusChart
            annotationStatuses={toolStatistics.allQuestionsStatistics}
            title={'all_questions'}
          />
        )}
        {toolStatisticsViewMode === ToolStatisticsViewMode.ALL_IMAGE_SETS && (
          <AllStatusChart
            annotationStatuses={toolStatistics.allImageSetsStatistics}
            title={'all_image_sets'}
            translateNames
          />
        )}
        {(toolStatisticsViewMode === ToolStatisticsViewMode.TOOL ||
          toolStatisticsViewMode === ToolStatisticsViewMode.QUESTION ||
          toolStatisticsViewMode === ToolStatisticsViewMode.IMAGE_SET) && (
          <>
            <AnnotationStatusChart
              annotationStatus={selectedToolStatistics?.annotationStatusStatistics}
              translateNames={toolStatisticsViewMode === ToolStatisticsViewMode.IMAGE_SET}
            />
            {selectedToolStatistics?.answerStatistics.map((as: IAnswerStatisticDto) => {
              return (
                <AnswersChart
                  key={as.id}
                  answerStatistics={as}
                  onGetAnswers={
                    toolStatisticsViewMode === ToolStatisticsViewMode.QUESTION
                      ? async (): Promise<void> => {
                          await projectDetailsStatisticsBl.getTextQuestionAnswers(as.id);
                        }
                      : async (): Promise<void> => {
                          await projectDetailsStatisticsBl.getTextAttributeAnswers({
                            attributeId: as.id,
                            annotationTypeId: selectedToolStatistics.annotationStatusStatistics.id,
                          });
                        }
                  }
                />
              );
            })}
          </>
        )}
      </div>
    </div>
  );
});
